import React, { useState, useEffect } from 'react';
import { SignInButton, SignUpButton, Show, UserButton } from '@clerk/react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Highlight, themes } from 'prism-react-renderer';
import {
  Upload, Zap, Shield, Code, Check, ArrowRight, ExternalLink,
  Globe, Share2, Terminal, Copy, Key, Link, Star,
  Users, Clock, Layers, ChevronRight, Info, Bot, Sparkles, Compass
} from 'lucide-react';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import HeroUpload from '@/components/HeroUpload';
const IntegrationsSection = React.lazy(() => import('@/components/IntegrationsSection'));
const PricingSection = React.lazy(() => import('@/components/PricingSection'));
import integrations from '@/data/integrations';
import usePageMeta from '@/hooks/usePageMeta';

const CodeBlock = ({ code, language, onCopy, copied }) => (
  <div className="relative">
    <button
      onClick={onCopy}
      aria-label={copied ? "Copied" : "Copy code"}
      className="absolute top-3 right-3 z-10 p-2 rounded-lg bg-white/10 hover:bg-white/20 text-gray-400 hover:text-white transition-colors"
    >
      {copied ? <Check className="w-4 h-4 text-success" /> : <Copy className="w-4 h-4" />}
    </button>
    <Highlight theme={themes.nightOwl} code={code} language={language}>
      {({ className, style, tokens, getLineProps, getTokenProps }) => (
        <pre className={`${className} text-sm rounded-2xl p-5 overflow-x-auto`} style={{ ...style, background: '#14161F' }}>
          {tokens && tokens.map((line, i) => (
            <div key={i} {...getLineProps({ line })}>
              {line.map((token, key) => (
                <span key={key} {...getTokenProps({ token })} />
              ))}
            </div>
          ))}
        </pre>
      )}
    </Highlight>
  </div>
);

export default function LandingPage() {
  const navigate = useNavigate();
  const [copiedTab, setCopiedTab] = useState(null);
  const [copiedCode, setCopiedCode] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState('annual');

  usePageMeta({
    title: 'Upload to URL - Public URL for Files & Instant Hosting',
    description: 'Get a Public URL for Files instantly. Upload any file and get direct links for n8n, Zapier, and Make automations. API-first file hosting with a free tier available.',
  });

  // Inject JSON-LD structured data for homepage
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Upload to URL",
      "url": "https://uploadtourl.com/"
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  // Inject FAQ structured data for homepage
  useEffect(() => {
    const faqData = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What file formats are supported?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Upload to URL supports all file formats including Images (JPG, PNG, GIF, JFIF, AVIF, WebP, SVG, ICO, PSD, AI), Videos (MP4, MOV, WebM, MPEG), Audio (MP3, WAV, OGG, OGA, OPUS, M4A, MPGA, M3U), Documents (PDF, DOCX, XLSX, ICS, LOG), Code & Data (JSON, XML, HTML, YAML, SQL, CSV, JSONL, TSV, RDP), and Archives (RAR, 7Z, TAR, GZ, BIN)."
          }
        },
        {
          "@type": "Question",
          "name": "What is the maximum file size I can upload?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The maximum file size depends on your plan: Free (5 MB), Starter (16 MB), and Pro (32 MB)."
          }
        },
        {
          "@type": "Question",
          "name": "Can I configure the expiry time of my files?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, file retention depends on your plan ranging from 7-day retention on the Free tier to 365 days on Pro. Paid plans can also set files to never expire."
          }
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'faq-json-ld';
    script.innerHTML = JSON.stringify(faqData);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('faq-json-ld');
      if (el) document.head.removeChild(el);
    };
  }, []);

  const askAiQuery = encodeURIComponent(
    "Tell me why Upload to URL app is a good choice for file upload and what it offers"
  ).replace(/%20/g, '+');

  const askAiLinks = [
    { name: 'ChatGPT', href: `https://chat.openai.com/?q=${askAiQuery}`, icon: Bot, tint: 'bg-muted text-foreground' },
    { name: 'Claude', href: `https://claude.ai/new?q=${askAiQuery}`, icon: Sparkles, tint: 'bg-brand-tint text-brand' },
    { name: 'Perplexity', href: `https://www.perplexity.ai/search/new?q=${askAiQuery}`, icon: Compass, tint: 'bg-teal-500/10 text-teal-600' },
    { name: 'Google AI', href: `https://www.google.com/search?q=${askAiQuery}&udm=50`, icon: Globe, tint: 'bg-blue-500/10 text-blue-600' },
  ];

  const preloadPage = (path) => {
    if (path === '/api-docs') {
      import('./ApiDocsPage');
    } else if (path === '/dashboard') {
      import('./DashboardPage');
    } else if (path === '/billing') {
      import('./BillingPage');
    }
  };

  const copyCode = (code, tab) => {
    navigator.clipboard.writeText(code);
    setCopiedTab(tab);
    setTimeout(() => setCopiedTab(null), 2000);
  };

  const curlExample = `curl -X POST https://uploadtourl.com/api/upload \\
  -H "x-api-key: uaf_xxx" \\
  -F "file=@photo.jpg"`;

  const pythonExample = `import requests

response = requests.post(
    "https://uploadtourl.com/api/upload",
    headers={"x-api-key": "uaf_xxx"},
    files={"file": open("photo.jpg", "rb")}
)
print(response.json()["url"])
# => https://cdn.uploadtourl.com/f/a1b2c3.jpg`;

  const jsExample = `const form = new FormData();
form.append("file", fileInput.files[0]);

const res = await fetch("https://uploadtourl.com/api/upload", {
  method: "POST",
  headers: { "x-api-key": "uaf_xxx" },
  body: form
});

const { url } = await res.json();
// => https://cdn.uploadtourl.com/f/a1b2c3.jpg`;

  const responseExample = `{
  "url": "https://cdn.uploadtourl.com/f/a1b2c3.jpg",
  "status": "success",
  "filename": "photo.jpg",
  "size": 245782,
  "credits_remaining": 94,
  "expires_at": "2026-03-28T12:00:00+00:00"
}`;

  const CdnTooltip = ({ children }) => (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="underline decoration-dotted decoration-brand/60 cursor-help">{children}</span>
      </TooltipTrigger>
      <TooltipContent className="max-w-[260px] text-center px-4 py-3">
        <p className="font-bold text-white mb-1">What's a CDN?</p>
        <p className="text-xs text-white/80 leading-relaxed">A Content Delivery Network stores copies of your file on servers around the world — so anyone who opens your link gets it from a nearby server, making it load fast no matter where they are.</p>
      </TooltipContent>
    </Tooltip>
  );

  return (
    <TooltipProvider delayDuration={200}>
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-10 pb-20 md:py-40 overflow-hidden" data-testid="hero-section">
        {/* Background effects */}
        <div className="absolute inset-0 grid-pattern" />
        <div className="absolute top-0 right-0 w-[700px] h-[700px] bg-gradient-to-bl from-brand/10 via-transparent to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tr from-brand/5 via-transparent to-transparent rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left column - copy & CTAs */}
            <div className="text-center lg:text-left">
              {/* Social Proof Section */}
              <div className="animate-fade-in-up mb-8 flex flex-col md:flex-row items-center justify-center lg:justify-start gap-x-6 gap-y-4">
                <div className="flex -space-x-3 overflow-visible px-2">
                  {[1, 2, 3, 4].map((i) => (
                    <img
                      key={i}
                      className="inline-block h-10 w-10 rounded-full border-2 border-background object-cover ring-1 ring-border shadow-soft"
                      src={`/assets/avatars/avatar${i}.webp`}
                      alt={`Developer Avatar ${i}`}
                      loading={i === 1 ? "eager" : "lazy"}
                      fetchpriority={i === 1 ? "high" : "auto"}
                    />
                  ))}
                </div>
                <div className="flex flex-col items-center md:items-start">
                  <div className="flex items-center gap-0.5 mb-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-sm font-medium text-muted-foreground text-center md:text-left">
                    Trusted by <span className="text-foreground font-bold">2000+</span> workflow experts
                  </p>
                </div>
              </div>

              <h1 className="animate-fade-in-up-delay-1 text-5xl md:text-7xl font-extrabold tracking-tight leading-[1.06] mb-8 text-foreground">
                Upload your File
                <br />
                <span className="text-brand">Get Public URL Instantly</span>
              </h1>

              <p className="animate-fade-in-up-delay-2 text-lg md:text-xl leading-relaxed text-muted-foreground mb-6 md:mb-12 max-w-2xl mx-auto lg:mx-0">
                Upload any file to get a direct, <CdnTooltip>CDN</CdnTooltip>-powered <b className="text-foreground">Public URL for Files</b>
                <span className="hidden md:inline"><br />Built for n8n, Make, Zapier & automation workflows.</span>
              </p>

              <div className="animate-fade-in-up-delay-3 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Show when="signed-out">
                  <SignUpButton mode="modal">
                    <Button
                      className="bg-brand hover:bg-brand/90 text-brand-foreground rounded-xl px-8 py-6 text-lg font-bold transition-all hover:shadow-glow active:scale-95"
                      data-testid="get-started-button-hero"
                    >
                      Get your free API Key
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </SignUpButton>
                </Show>
                <Show when="signed-in">
                  <Button
                    onClick={() => navigate('/dashboard')}
                    className="bg-brand hover:bg-brand/90 text-brand-foreground rounded-xl px-8 py-6 text-lg font-bold transition-all hover:shadow-glow active:scale-95"
                    data-testid="go-to-dashboard-button"
                  >
                    Go to Dashboard
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Show>
                <Button
                  variant="outline"
                  className="border-border bg-card text-foreground hover:bg-accent rounded-xl px-8 py-6 text-lg font-semibold font-mono transition-colors active:scale-95"
                  onClick={() => document.getElementById('api-section')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  <Terminal className="w-5 h-5 mr-2" />
                  View API
                </Button>
              </div>
              {/* Works with Section */}
              <div className="animate-fade-in-up-delay-3 mt-8 flex flex-wrap items-center justify-center lg:justify-start gap-4 md:gap-6 opacity-90">
                <div className="flex items-center gap-2 text-xs font-bold text-muted-foreground uppercase tracking-widest">
                  <Layers className="w-4 h-4 text-brand" />
                  <span>Works with</span>
                </div>
                <div className="flex items-center gap-4 md:gap-5">
                  <RouterLink to={`/${integrations.n8n.slug}`} title="n8n" className="transition-transform hover:scale-110 duration-200">
                    <img src={integrations.n8n.logoPath} className="h-7 w-auto" alt="n8n" />
                  </RouterLink>
                  <RouterLink to={`/${integrations.zapier.slug}`} title="Zapier" className="transition-transform hover:scale-110 duration-200">
                    <img src={integrations.zapier.logoPath} className="h-7 w-auto" alt="Zapier" />
                  </RouterLink>
                  <RouterLink to={`/${integrations.make.slug}`} title="Make" className="transition-transform hover:scale-110 duration-200">
                    <img src={integrations.make.logoPath} className="h-7 w-auto" alt="Make" />
                  </RouterLink>
                  <RouterLink to={`/${integrations.pipedream.slug}`} title="Pipedream" className="transition-transform hover:scale-110 duration-200">
                    <img src={integrations.pipedream.logoPath} className="h-7 w-auto" alt="Pipedream" />
                  </RouterLink>
                  <RouterLink to="/integrations" className="text-sm font-bold text-brand hover:opacity-80 transition-opacity border-b-2 border-transparent hover:border-brand/30 pb-0.5 ml-1">
                    and more..
                  </RouterLink>
                </div>
              </div>
            </div>

            {/* Right column - upload showcase */}
            <div className="animate-fade-in-up-delay-3" data-testid="hero-upload">
              <div className="flex items-center justify-between mb-4 px-1">
                <span className="text-sm font-bold text-foreground">Try it right now</span>
                <Show when="signed-out">
                  <span className="text-xs font-bold text-success bg-success-bg px-3 py-1 rounded-full">
                    No signup needed
                  </span>
                </Show>
              </div>

              <HeroUpload source="website" />

              <div className="grid grid-cols-3 gap-3 mt-4">
                {[
                  { value: '< 2s', label: 'Avg Upload', cdn: false },
                  { value: 'CDN', label: 'Global Edge', cdn: true },
                  { value: '99.9%', label: 'Uptime', cdn: false },
                ].map((stat, i) => (
                  <div key={i} className="bg-card border border-border rounded-xl py-3 px-2 text-center shadow-soft">
                    {stat.cdn ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <p className="text-base font-extrabold text-foreground underline decoration-dotted decoration-muted-foreground/50 cursor-help">{stat.value}</p>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[260px] text-center px-4 py-3">
                          <p className="font-bold text-white mb-1">What's a CDN?</p>
                          <p className="text-xs text-white/80 leading-relaxed">A Content Delivery Network stores copies of your file on servers worldwide — so your link loads fast for everyone, everywhere.</p>
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <p className="text-base font-extrabold text-foreground">{stat.value}</p>
                    )}
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mt-0.5">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Strip */}
      <section className="py-16 border-y border-border bg-card" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            {[
              { value: '99.9%', label: 'Uptime SLA', subtext: 'Reliability you can bake into your production workflows.', icon: Clock },
              { value: '< 2s', label: 'Time to URL', subtext: 'Optimized for high-speed API uploads and instant CDN delivery.', icon: Zap },
              { value: '0', label: 'Redirects', subtext: 'Pure, raw public URLs that never break your downstream nodes.', icon: Share2 },
              { value: 'JSON', label: 'Native Output', subtext: 'Clean API responses designed to be parsed easily by n8n and Make.', icon: Code },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-brand-tint mb-4">
                  <stat.icon className="w-5 h-5 text-brand" />
                </div>
                <p className="text-3xl md:text-4xl font-extrabold text-foreground mb-1">
                  {stat.value}
                </p>
                <p className="text-sm text-muted-foreground font-semibold uppercase tracking-wider">{stat.label}</p>
                {stat.subtext && (
                  <p className="text-xs text-muted-foreground/80 mt-2 leading-relaxed px-2">{stat.subtext}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 md:py-32 relative overflow-hidden" data-testid="how-it-works-section">
        <div className="absolute inset-0 grid-pattern opacity-10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-20">
            <p className="text-sm font-bold text-brand uppercase tracking-widest mb-4">Simple Setup</p>
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-foreground">
              Public URL for Files in Three Steps
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                step: '01',
                icon: Key,
                title: 'Get Your API Key',
                description: 'Sign up for free and generate an API key from your dashboard in seconds.',
              },
              {
                step: '02',
                icon: Terminal,
                title: 'Upload File',
                description: 'Send a POST request with your file. Works with n8n, cURL, Python, JavaScript, or any HTTP client.',
              },
              {
                step: '03',
                icon: Link,
                title: 'Use Your URL',
                description: null,
                descriptionJsx: true,
              },
            ].map((item, i) => (
              <div key={i} className="relative text-center md:text-left group">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-tint mb-6 transition-colors">
                  <item.icon className="w-7 h-7 text-brand" />
                </div>
                <p className="text-sm font-bold text-brand mb-2 tracking-wider">{item.step}</p>
                <h3 className="text-xl font-bold text-foreground mb-3">{item.title}</h3>
                {item.descriptionJsx ? (
                  <p className="text-muted-foreground leading-relaxed">
                    Get back a direct, public <CdnTooltip>CDN</CdnTooltip> URL. Use it anywhere — social media, HTML, automation.
                  </p>
                ) : (
                  <p className="text-muted-foreground leading-relaxed">{item.description}</p>
                )}
                {i < 2 && (
                  <div className="hidden md:block absolute top-8 -right-4 w-8">
                    <ChevronRight className="w-6 h-6 text-muted-foreground/50" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 md:py-32 bg-card relative grain-overlay" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-20">
            <p className="text-sm font-bold text-brand uppercase tracking-widest mb-4">Core Features</p>
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-6 text-foreground">
              Why Choose Upload to URL?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Enterprise-grade file hosting with developer-friendly API access
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: Code,
                title: 'Simple API',
                description: 'RESTful API with clear documentation. Upload files with a single POST request and get instant public URLs. No SDK required.',
              },
              {
                icon: Zap,
                title: 'Lightning Fast',
                description: null,
                descriptionJsx: true,
              },
              {
                icon: Shield,
                title: 'Secure & Reliable',
                description: 'Enterprise-grade security with encrypted storage. 99.9% uptime guarantee backed by AWS infrastructure and automatic backups. Fully GDPR Compliant.',
              },
            ].map((feature, i) => (
              <div
                key={i}
                className="bg-background border border-border rounded-2xl p-8 shadow-soft hover:shadow-card hover:border-brand/40 transition-shadow duration-300 group"
                data-testid={`feature-${['api', 'fast', 'secure'][i]}`}
              >
                <div className="w-14 h-14 rounded-xl bg-brand-tint flex items-center justify-center mb-6 transition-colors">
                  <feature.icon className="w-7 h-7 text-brand" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-foreground">{feature.title}</h3>
                {feature.descriptionJsx ? (
                  <p className="text-muted-foreground leading-relaxed">
                    Powered by AWS S3 + CloudFront <CdnTooltip>CDN</CdnTooltip>. Your files load instantly from edge locations around the world with sub-100ms response times.
                  </p>
                ) : (
                  <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* API Usage Section */}
      <section id="api-section" className="py-24 md:py-32 relative overflow-hidden" data-testid="api-usage-section">
        <div className="absolute inset-0 grid-pattern opacity-10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Left side - copy */}
            <div>
              <p className="text-sm font-bold text-brand uppercase tracking-widest mb-4">Developer API</p>
              <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-6 text-foreground">
                One API Call.<br />Instant URL.
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                No complex SDKs, no configuration headaches. Send a file, get a URL.
                Works with any programming language that supports HTTP requests.
              </p>

              <div className="space-y-5">
                {[
                  { icon: Zap, text: 'Single endpoint - POST your file, get a URL back', cdnText: null },
                  { icon: Globe, text: null, cdnText: 'Direct CDN URLs work everywhere - no redirect pages' },
                  { icon: Shield, text: 'API key authentication - simple and secure', cdnText: null },
                  { icon: Layers, text: 'JSON response with URL, size, and metadata', cdnText: null },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-brand-tint flex items-center justify-center flex-shrink-0 mt-0.5">
                      <item.icon className="w-4 h-4 text-brand" />
                    </div>
                    {item.cdnText ? (
                      <p className="text-foreground/80">Direct <CdnTooltip>CDN</CdnTooltip> URLs work everywhere — no redirect pages</p>
                    ) : (
                      <p className="text-foreground/80">{item.text}</p>
                    )}
                  </div>
                ))}
              </div>

              <Button
                onClick={() => navigate('/api-docs')}
                className="bg-brand hover:bg-brand/90 text-brand-foreground rounded-xl px-8 py-4 font-bold transition-all hover:shadow-glow mt-8"
              >
                View API Docs
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>

            {/* Right side - code tabs */}
            <div className="w-full max-w-full overflow-hidden">
              <Tabs defaultValue="curl" className="w-full">
                <TabsList className="bg-card border border-border p-1 rounded-xl w-full justify-start gap-1 h-auto flex-wrap overflow-x-auto no-scrollbar">
                  {[
                    { value: 'curl', label: 'cURL' },
                    { value: 'python', label: 'Python' },
                    { value: 'javascript', label: 'JavaScript' },
                    { value: 'response', label: 'Response' },
                  ].map((tab) => (
                    <TabsTrigger
                      key={tab.value}
                      value={tab.value}
                      className="rounded-lg text-muted-foreground data-[state=active]:bg-brand data-[state=active]:text-brand-foreground data-[state=active]:shadow-none px-4 py-2 text-sm font-semibold"
                    >
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
                <TabsContent value="curl" className="mt-3">
                  <CodeBlock code={curlExample} language="bash" onCopy={() => copyCode(curlExample, 'curl')} copied={copiedTab === 'curl'} />
                </TabsContent>
                <TabsContent value="python" className="mt-3">
                  <CodeBlock code={pythonExample} language="python" onCopy={() => copyCode(pythonExample, 'python')} copied={copiedTab === 'python'} />
                </TabsContent>
                <TabsContent value="javascript" className="mt-3">
                  <CodeBlock code={jsExample} language="javascript" onCopy={() => copyCode(jsExample, 'javascript')} copied={copiedTab === 'javascript'} />
                </TabsContent>
                <TabsContent value="response" className="mt-3">
                  <CodeBlock code={responseExample} language="json" onCopy={() => copyCode(responseExample, 'response')} copied={copiedTab === 'response'} />
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>
      </section>
      <React.Suspense fallback={<div className="h-96 animate-pulse bg-card" />}>
        <IntegrationsSection />
      </React.Suspense>

      {/* Direct URLs Advantage */}
      <section className="py-24 md:py-32 relative overflow-hidden" data-testid="direct-urls-section">
        <div className="absolute inset-0 grid-pattern opacity-10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-20">
            <p className="text-sm font-bold text-brand uppercase tracking-widest mb-4">Direct URLs</p>
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-6 text-foreground">
              No Redirect Pages. No Login Walls.
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Unlike cloud storage sharing links, Upload to URL gives you direct URLs
              that work everywhere.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Share2,
                title: 'Social Media',
                description: 'Direct links display rich previews on Twitter, Facebook, LinkedIn, and Discord. No "Open in Drive" pages.',
              },
              {
                icon: Code,
                title: 'HTML Embedding',
                description: 'Use URLs in <img src>, <video>, <a href> tags. Works in websites, emails, and CMS platforms.',
              },
              {
                icon: Zap,
                title: 'Automation',
                description: 'Feed URLs directly into n8n, Zapier, Make, or custom API workflows without extra processing.',
              },
              {
                icon: Globe,
                title: 'Documentation',
                description: 'Embed files in wikis, READMEs, Notion pages, and internal docs with clean, permanent URLs.',
              },
            ].map((useCase, i) => (
              <div key={i} className="bg-card border border-border rounded-2xl p-7 shadow-soft hover:shadow-card hover:border-brand/40 transition-shadow duration-300 group">
                <div className="w-11 h-11 rounded-lg bg-brand-tint flex items-center justify-center mb-5 transition-colors">
                  <useCase.icon className="w-5 h-5 text-brand" />
                </div>
                <h3 className="text-lg font-bold text-foreground mb-2">{useCase.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{useCase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-24 md:py-32 bg-card relative grain-overlay" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      <React.Suspense fallback={<div className="h-96 animate-pulse bg-background" />}>
        <PricingSection
          compact={true}
          showFooter={false}
          title="Simple, Transparent Pricing"
          subtitle="Start free. Upgrade when you need more. No hidden fees."
        />
      </React.Suspense>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="pt-24 md:pt-32 pb-8 md:pb-12 bg-background relative" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-sm font-bold text-brand uppercase tracking-widest mb-4">FAQ</p>
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-6 text-foreground">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Everything you need to know about Upload to URL
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-3">
            {[
              {
                value: 'formats',
                question: 'What file formats are supported?',
                answer: (
                  <div className="space-y-4">
                    <p>Upload to URL supports <strong className="text-foreground">all common file formats</strong>. Here's the full list:</p>
                    <div className="space-y-3">
                      {[
                        { label: '🖼️ Images', formats: ['JPG', 'PNG', 'GIF', 'JFIF', 'AVIF', 'WebP', 'SVG', 'ICO', 'PSD', 'AI'] },
                        { label: '🎥 Videos', formats: ['MP4', 'MOV', 'WebM', 'MPEG'] },
                        { label: '🎵 Audio', formats: ['MP3', 'WAV', 'OGG', 'OGA', 'OPUS', 'M4A', 'MPGA', 'M3U'] },
                        { label: '📄 Documents', formats: ['PDF', 'DOCX', 'XLSX', 'ICS', 'LOG'] },
                        { label: '💻 Code & Data', formats: ['JSON', 'XML', 'HTML', 'YAML', 'SQL', 'CSV', 'JSONL', 'TSV', 'RDP'] },
                        { label: '🗜️ Archives', formats: ['RAR', '7Z', 'TAR', 'GZ', 'BIN'] },
                      ].map(({ label, formats }) => (
                        <div key={label}>
                          <p className="text-xs font-bold text-foreground uppercase tracking-wider mb-1.5">{label}</p>
                          <div className="flex flex-wrap gap-1.5">
                            {formats.map(fmt => (
                              <span key={fmt} className="inline-flex items-center px-2 py-0.5 rounded-md bg-brand-tint text-brand text-xs font-mono font-semibold border border-brand/20">{fmt}</span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm">If your system can send it over HTTP, we can host it and give you a direct URL.</p>
                  </div>
                ),
              },
              {
                value: 'file-size',
                question: 'What is the maximum file size I can upload?',
                answer: (
                  <div className="space-y-2">
                    <p>The maximum file size depends on your plan:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li><strong className="text-foreground">Free</strong> - 5 MB (per upload) & 500 MB total storage</li>
                      <li><strong className="text-foreground">Starter</strong> (from $8/month) - 16 MB (per upload) & 10 GB total storage</li>
                      <li><strong className="text-foreground">Pro</strong> (from $29/month) - 32 MB (per upload) & 50 GB total storage</li>
                      <li><strong className="text-foreground">Business</strong> (from $74/month) - 100 MB (per upload) & 500 GB total storage</li>
                    </ul>
                    <p>Need larger files? <button onClick={() => window.$zoho?.salesiq?.floatwindow?.visible('show')} className="text-brand hover:underline font-semibold">Contact us</button> and we can work out a custom plan for your use case.</p>
                  </div>
                ),
              },
              {
                value: 'expiry',
                question: 'Can I configure the expiry time of my files?',
                answer: (
                  <div className="space-y-2">
                    <p>Yes. File retention depends on your plan:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li><strong className="text-foreground">Free</strong> - 7-day retention</li>
                      <li><strong className="text-foreground">Starter</strong> - 90-day default retention</li>
                      <li><strong className="text-foreground">Pro</strong> - 365-day default retention</li>
                    </ul>
                    <p>On paid plans, you can also set files to <strong className="text-foreground">never expire</strong> - so your URLs stay live for as long as you need them.</p>
                  </div>
                ),
              },
              {
                value: 'delete',
                question: 'Can I delete my files anytime?',
                answer: (
                  <div className="space-y-2">
                    <p>Absolutely. We provide a delete API that lets you remove any of your uploaded files at any time, instantly.</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>Once deleted, the file is <strong className="text-foreground">permanently removed</strong> from our storage and <CdnTooltip>CDN</CdnTooltip></li>
                      <li>You have full control over your data</li>
                    </ul>
                  </div>
                ),
              },
              {
                value: 'security',
                question: 'What security practices do you follow?',
                answer: (
                  <div className="space-y-4">
                    <p>We take security seriously. Here's what we do:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>All files stored on <strong className="text-foreground">AWS S3</strong> with server-side encryption</li>
                      <li>Delivered through <strong className="text-foreground">CloudFront CDN</strong> over HTTPS</li>
                      <li>API access authenticated via unique API keys</li>
                      <li>Delete your files at any time through our API</li>
                      <li>Infrastructure runs on AWS with enterprise-grade reliability and redundancy</li>
                      <li>Fully <strong className="text-foreground">GDPR Compliant</strong> for data privacy and protection</li>
                    </ul>
                    <p>Read more about our infrastructure guarantees on our <a href="/security" className="text-brand hover:underline font-semibold">Security & Trust</a> page.</p>
                  </div>
                ),
              },
              {
                value: 'integrations',
                question: 'What integrations are supported?',
                answer: (
                  <div className="space-y-4">
                    <div>
                      <p className="font-bold text-foreground mb-2">Live Integrations:</p>
                      <ul className="list-disc list-inside space-y-1 ml-2 text-muted-foreground">
                        <li><strong className="text-foreground">REST API</strong> - Works anywhere you can make an HTTP request.</li>
                        <li><strong className="text-foreground">n8n</strong> - Official Cloud Node.</li>
                        <li><strong className="text-foreground">Zapier</strong> - App implementation available.</li>
                        <li><strong className="text-foreground">Make (Integromat)</strong> - Direct integration.</li>
                        <li><strong className="text-foreground">Pipedream</strong> - Connect your workflows seamlessly.</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-bold text-foreground mb-2">Coming Soon:</p>
                      <ul className="list-disc list-inside space-y-1 ml-2 text-muted-foreground">
                        <li><strong className="text-foreground">Airtable</strong> - Fix those expiring attachment URLs.</li>
                        <li><strong className="text-foreground">Monday.com</strong> - Public links for board files.</li>
                        <li><strong className="text-foreground">ClickUp</strong> - Share task attachments directly.</li>
                      </ul>
                    </div>
                  </div>
                ),
              },
              {
                value: 'cancel',
                question: 'Can I cancel my subscription anytime?',
                answer: (
                  <div className="space-y-2">
                    <p>Yes, you can cancel your subscription at any time from your billing dashboard - <strong className="text-foreground">no questions asked, no long-term contracts</strong>.</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>After cancellation, you'll continue to have access until the end of your current billing period</li>
                      <li>Check our <a href="/refund" className="text-brand hover:underline">refund policy</a> for more details</li>
                    </ul>
                  </div>
                ),
              },
              {
                value: 'rate-limit',
                question: 'Is there a rate limit on API requests?',
                answer: (
                  <div className="space-y-2">
                    <p>Each plan comes with a monthly upload quota:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li><strong className="text-foreground">Free</strong> - 15 uploads/month</li>
                      <li><strong className="text-foreground">Starter</strong> - 1,000 uploads/month</li>
                      <li><strong className="text-foreground">Pro</strong> - 6,000 uploads/month</li>

                    </ul>
                    <p>Within those limits, you can upload as fast as your network allows - our API is optimized for high-speed automation workflows with <strong className="text-foreground">sub-2-second response times</strong>.</p>
                  </div>
                ),
              },
              {
                value: 'support',
                question: 'How do I contact support?',
                answer: (
                  <div className="space-y-2">
                    <p>You can reach our support team anytime:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li><button onClick={() => window.$zoho?.salesiq?.floatwindow?.visible('show')} className="text-brand hover:underline font-semibold">Chat with Support</button> directly on this page</li>
                      <li>Email us at <strong className="text-foreground">support@uploadtourl.com</strong></li>
                      <li>We typically respond within <strong className="text-foreground">24 hours</strong></li>
                      <li>Find us on LinkedIn and GitHub for community support and updates</li>
                    </ul>
                  </div>
                ),
              },
            ].map((faq) => (
              <AccordionItem
                key={faq.value}
                value={faq.value}
                className="bg-card border border-border rounded-2xl px-6 overflow-hidden data-[state=open]:border-brand/40 transition-colors"
              >
                <AccordionTrigger className="text-base font-semibold text-foreground hover:no-underline hover:text-brand py-5 [&>svg]:text-brand">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="pt-8 md:pt-12 pb-24 md:pb-32 bg-background relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 grid-pattern opacity-20" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-brand/10 via-transparent to-transparent rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-extrabold text-foreground mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
            Join developers worldwide using 'Upload to URL' for reliable, <CdnTooltip>CDN</CdnTooltip>-backed file hosting with direct public URLs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Show when="signed-out">
              <SignUpButton mode="modal">
                <Button
                  className="bg-brand hover:bg-brand/90 text-brand-foreground rounded-xl px-8 py-6 text-lg font-bold transition-all hover:shadow-glow active:scale-95"
                  data-testid="get-started-button-cta"
                >
                  Start Uploading Now
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </SignUpButton>
            </Show>
            <Show when="signed-in">
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-brand hover:bg-brand/90 text-brand-foreground rounded-xl px-8 py-6 text-lg font-bold transition-all hover:shadow-glow active:scale-95"
                data-testid="dashboard-button-cta"
              >
                Go to Dashboard
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Show>
            <a
              href="https://n8n.io/integrations/upload-to-url/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-card hover:bg-accent text-foreground border border-border rounded-xl px-8 py-3.5 text-lg font-semibold transition-colors"
            >
              <Share2 className="w-5 h-5" />
              n8n Integration
            </a>
          </div>
        </div>
      </section>

      {/* Ask AI Section */}
      <section className="pb-24 md:pb-32 bg-background relative" data-testid="ask-ai-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-card border border-border rounded-3xl p-8 md:p-12 shadow-soft grid md:grid-cols-2 gap-8 md:gap-10 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-foreground mb-3">
                Scrolled so much, still not sure?
              </h2>
              <p className="text-muted-foreground text-lg">
                Let your favorite AI tell you if Upload to URL is the right fit.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {askAiLinks.map((aiLink) => (
                <a
                  key={aiLink.name}
                  href={aiLink.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2.5 bg-background border border-border rounded-xl px-4 py-3.5 font-semibold text-sm text-foreground hover:border-brand/40 hover:shadow-card transition-all"
                  data-testid={`ask-ai-${aiLink.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${aiLink.tint}`}>
                    <aiLink.icon className="w-4 h-4" />
                  </div>
                  {aiLink.name}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
    </TooltipProvider>
  );
}
